/**
 * Owner: Masa Bayu
 * File: qx-draft-outline.ts
 * Date:Time: 2026-10-15 23:54
 * Description: Live Outline · jump · body find cycle · find-hit paint.
 */

import { qxwHasFixedPages, type QXTextContent, type QXTextInline, type QXWDocument } from '@qx/qxw-core';
import { countSpinePages } from './qx-writer-book-spine';

function leafPlain(inlines: readonly QXTextInline[]): string {
  return inlines
    .map((inline) => (inline.type === 'note-ref' ? '' : inline.text))
    .join('');
}

export type OutlineNode = {
  readonly id: string;
  readonly label: string;
  readonly children?: readonly OutlineNode[];
};

function slugLabel(text: string): string {
  const trimmed = text.replace(/\s+/g, ' ').trim();
  return trimmed.length > 0 ? trimmed : 'Untitled';
}

function ensureId(el: HTMLElement, index: number): string {
  const existing = el.getAttribute('id')?.trim();
  if (existing) return existing;
  const id = `outline_${index}`;
  el.setAttribute('id', id);
  return id;
}

/** Assign stable ids to heading elements missing `id` (for Outline jump). */
export function ensureDraftHeadingIds(root: HTMLElement): void {
  let index = 0;
  for (const el of Array.from(root.querySelectorAll('h1, h2, h3'))) {
    if (!(el instanceof HTMLElement)) continue;
    ensureId(el, index);
    index += 1;
  }
}

type MutableH = { id: string; label: string; children: OutlineNode[] };

function buildTree(
  headings: readonly { id: string; label: string; level: number }[],
): readonly OutlineNode[] {
  const roots: OutlineNode[] = [];
  let currentH1: MutableH | null = null;
  let currentH2: MutableH | null = null;

  const flushH2 = () => {
    if (!currentH2 || !currentH1) return;
    currentH1.children.push(
      currentH2.children.length > 0
        ? { id: currentH2.id, label: currentH2.label, children: currentH2.children }
        : { id: currentH2.id, label: currentH2.label },
    );
    currentH2 = null;
  };

  const flushH1 = () => {
    flushH2();
    if (!currentH1) return;
    roots.push(
      currentH1.children.length > 0
        ? { id: currentH1.id, label: currentH1.label, children: currentH1.children }
        : { id: currentH1.id, label: currentH1.label },
    );
    currentH1 = null;
  };

  for (const heading of headings) {
    if (heading.level <= 1) {
      flushH1();
      currentH1 = { id: heading.id, label: heading.label, children: [] };
      continue;
    }
    if (heading.level === 2) {
      if (!currentH1) {
        currentH1 = { id: `${heading.id}_root`, label: 'Document', children: [] };
      }
      flushH2();
      currentH2 = { id: heading.id, label: heading.label, children: [] };
      continue;
    }
    if (currentH2) currentH2.children.push({ id: heading.id, label: heading.label });
    else if (currentH1) currentH1.children.push({ id: heading.id, label: heading.label });
    else roots.push({ id: heading.id, label: heading.label });
  }
  flushH1();
  return roots;
}

/**
 * Build Outline tree: h1 roots · h2 under last h1 · h3 under last h2 (or h1).
 */
export function outlineFromEditor(root: HTMLElement): readonly OutlineNode[] {
  ensureDraftHeadingIds(root);
  const headings: { id: string; label: string; level: number }[] = [];
  let index = 0;
  for (const el of Array.from(root.querySelectorAll('h1, h2, h3'))) {
    if (!(el instanceof HTMLElement)) continue;
    const tag = el.tagName.toLowerCase();
    const level = tag === 'h1' ? 1 : tag === 'h2' ? 2 : 3;
    headings.push({
      id: ensureId(el, index),
      label: slugLabel(el.textContent ?? ''),
      level,
    });
    index += 1;
  }
  return buildTree(headings);
}

/** Tip headings → Outline (ids match compiler `${elementId}_b${index}`). */
export function outlineFromTextContent(
  content: QXTextContent,
  sourceElementId: string,
): readonly OutlineNode[] {
  const headings: { id: string; label: string; level: number }[] = [];
  content.blocks.forEach((block, blockIndex) => {
    if (block.type !== 'heading') return;
    headings.push({
      id: `${sourceElementId}_b${blockIndex}`,
      label: slugLabel(leafPlain(block.inlines)),
      level: block.level <= 3 ? block.level : 3,
    });
  });
  return buildTree(headings);
}

export function outlineFromDocument(doc: QXWDocument): readonly OutlineNode[] {
  if (qxwHasFixedPages(doc)) {
    return doc.pages
      .filter((page) => page.elementIds.length > 0)
      .map((page, index) => ({ id: page.id, label: `Page ${index + 1}` }));
  }
  const merged: OutlineNode[] = [];
  for (const element of doc.elements) {
    if (element.type !== 'text') continue;
    merged.push(...outlineFromTextContent(element.content, element.id));
  }
  return merged;
}

/** Prefer live editor headings; fall back to tip blocks (Import / reopen). */
export function resolveOutlineNodes(
  root: HTMLElement | null,
  doc: QXWDocument,
): readonly OutlineNode[] {
  if (root) {
    const fromDom = outlineFromEditor(root);
    if (fromDom.length > 0) return fromDom;
  }
  return outlineFromDocument(doc);
}

export function findOutlineLabel(
  nodes: readonly OutlineNode[],
  id: string,
): string | null {
  for (const node of nodes) {
    if (node.id === id) return node.label;
    if (node.children) {
      const nested = findOutlineLabel(node.children, id);
      if (nested) return nested;
    }
  }
  return null;
}

export type QueryJumpResult = {
  readonly ok: boolean;
  readonly matchIndex: number;
  readonly matchCount: number;
};

const FIND_HIT = 'qxw-find-hit';
const FIND_ACTIVE = 'qxw-find-hit--active';

/** Remove ephemeral find paint (session DOM only · ≠ tip). */
export function clearEditorFindHits(root: HTMLElement): void {
  root.querySelectorAll(`.${FIND_HIT}`).forEach((el) => {
    el.classList.remove(FIND_HIT, FIND_ACTIVE);
  });
}

/** Collect unique parent elements that contain the query (case-insensitive). */
export function collectEditorQueryHits(root: HTMLElement, query: string): HTMLElement[] {
  const needle = query.trim().toLowerCase();
  if (needle.length < 2) return [];
  const hits: HTMLElement[] = [];
  const seen = new Set<HTMLElement>();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    const text = current.textContent ?? '';
    const parent = current.parentElement;
    if (parent && text.toLowerCase().includes(needle) && !seen.has(parent)) {
      seen.add(parent);
      hits.push(parent);
    }
    current = walker.nextNode();
  }
  return hits;
}

/** Jump to matchIndex (wraps). Paints all hits · active current. */
export function jumpEditorToQuery(
  root: HTMLElement,
  query: string,
  matchIndex = 0,
): QueryJumpResult {
  clearEditorFindHits(root);
  const hits = collectEditorQueryHits(root, query);
  if (hits.length === 0) return { ok: false, matchIndex: -1, matchCount: 0 };
  const i = ((matchIndex % hits.length) + hits.length) % hits.length;
  for (const hit of hits) hit.classList.add(FIND_HIT);
  const active = hits[i]!;
  active.classList.add(FIND_ACTIVE);
  active.scrollIntoView({ behavior: 'smooth', block: 'center' });
  return { ok: true, matchIndex: i, matchCount: hits.length };
}

/** Jump by element id, else first text match of Outline label. */
export function jumpEditorToOutlineId(
  root: HTMLElement,
  id: string,
  nodes: readonly OutlineNode[],
): boolean {
  const byId = root.querySelector(`#${CSS.escape(id)}`);
  if (byId instanceof HTMLElement) {
    byId.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  }
  const label = findOutlineLabel(nodes, id);
  if (!label) return false;
  return jumpEditorToQuery(root, label).ok;
}

/** Tap Outline Page N · nombor 1-based dari `page.id`, atau null. */
export function pageLabelForOutlineId(doc: QXWDocument, id: string): string | null {
  if (!qxwHasFixedPages(doc)) return null;
  const pages = doc.pages.filter((page) => page.elementIds.length > 0);
  const index = pages.findIndex((page) => page.id === id);
  return index < 0 ? null : String(index + 1);
}

export function jumpWriterOutline(
  id: string,
  doc: QXWDocument,
  setViewPage: (label: string) => void,
  host: Element | null,
  nodes: readonly OutlineNode[],
): void {
  const pageLabel = pageLabelForOutlineId(doc, id);
  if (pageLabel) {
    setViewPage(pageLabel);
    return;
  }
  if (host instanceof HTMLElement) jumpEditorToOutlineId(host, id, nodes);
}

/** Honest status pages: spine sheets · fixed tip · else flow draft → 1. */
export function pageCountFromDocument(
  doc: QXWDocument,
  editor?: HTMLElement | null,
): number {
  const spinePages = countSpinePages(editor, doc);
  if (spinePages > 0) return spinePages;
  const fixed = doc.pages.filter((page) => page.elementIds.length > 0).length;
  if (fixed > 0) return fixed;
  return 1;
}
